window.AdminIndustries = function AdminIndustries({ showNotification }) {
  const [industriesData, setIndustriesData] = React.useState([]);
  const [availableProducts, setAvailableProducts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [editingIndustry, setEditingIndustry] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Media Picker Config
  const [mediaPickerConfig, setMediaPickerConfig] = React.useState({ isOpen: false, onSelect: null, title: 'Choose Image', targetField: null });

  React.useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const res = await window.apiClient.get('content.php');
      if (res.success && res.data) {
        if (res.data.industries_data) {
          setIndustriesData(res.data.industries_data);
        } else {
          setIndustriesData([]);
        }
      } else {
        setIndustriesData([]);
      }
      
      // Fetch products from products.php to get items from "Product & Engineering Dossiers Management"
      try {
        const prodRes = await window.apiClient.get('products.php?admin=1');
        if (prodRes.success && prodRes.data) {
          setAvailableProducts(prodRes.data);
        } else {
          setAvailableProducts([]);
        }
      } catch(e) {
        setAvailableProducts([]);
      }

      // Fetch categories
      try {
        const catRes = await window.apiClient.get('categories.php');
        if (catRes.success && catRes.data) {
          setCategories(catRes.data);
        }
      } catch (e) {
        console.warn('Failed to fetch categories:', e);
      }
      
    } catch (e) {
      console.error(e);
      showNotification('Failed to load industries data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIndustries = async () => {
    try {
      setSaving(true);
      const res = await window.apiClient.post('content.php', {
        section_key: 'industries_data',
        data: industriesData
      });
      if (res.success) {
        showNotification('Industries saved successfully!');
        window.dispatchEvent(new Event('novatek_content_updated'));
      } else {
        throw new Error(res.message || 'Failed to save');
      }
    } catch (e) {
      showNotification(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveIndustry = (index, direction) => {
    const newData = [...industriesData];
    const targetIdx = index + direction;
    if (targetIdx >= 0 && targetIdx < newData.length) {
      [newData[index], newData[targetIdx]] = [newData[targetIdx], newData[index]];
      setIndustriesData(newData);
    }
  };

  const handleToggleIndustryVisibility = (index) => {
    const newData = [...industriesData];
    newData[index].visible = newData[index].visible === false ? true : false;
    setIndustriesData(newData);
  };

  const handleDeleteIndustry = (index) => {
    if (window.confirm("Are you sure you want to delete this industry sector? This cannot be undone until saved.")) {
      const newData = [...industriesData];
      newData.splice(index, 1);
      setIndustriesData(newData);
    }
  };

  const handleSaveEdit = () => {
    if (!editingIndustry || !editingIndustry.data) return;
    const newData = [...industriesData];
    if (editingIndustry.index !== undefined) {
      newData[editingIndustry.index] = editingIndustry.data;
    } else {
      newData.push(editingIndustry.data);
    }
    setIndustriesData(newData);
    setEditingIndustry(null);
  };

  const openMediaPicker = (field) => {
    let title = 'Choose Image';
    if (field === 'primary_image') title = 'Select Primary Image';
    else if (field === 'gallery') title = 'Add Gallery Image';
    else if (field.startsWith('subsector_image_')) title = 'Select Sub-Sector Cover Image';

    setMediaPickerConfig({
      isOpen: true,
      title,
      targetField: field,
      onSelect: (filepath) => {
        if (!editingIndustry) return;
        const currentData = { ...editingIndustry.data };
        
        if (field === 'primary_image') {
          currentData.primary_image = filepath;
        } else if (field === 'gallery') {
          currentData.gallery = [...(currentData.gallery || []), filepath];
        } else if (field.startsWith('subsector_image_')) {
          const idx = parseInt(field.split('_').pop(), 10);
          if (currentData.subSectors && currentData.subSectors[idx]) {
            currentData.subSectors[idx].image = filepath;
          }
        }
        
        setEditingIndustry({
          ...editingIndustry,
          data: currentData
        });
      }
    });
  };

  const removeGalleryImage = (idxToRemove) => {
    if (!editingIndustry || !editingIndustry.data.gallery) return;
    const newGallery = editingIndustry.data.gallery.filter((_, idx) => idx !== idxToRemove);
    setEditingIndustry({
      ...editingIndustry,
      data: { ...editingIndustry.data, gallery: newGallery }
    });
  };

  // Lock body and main content scroll when modal is open
  React.useEffect(() => {
    const mainEl = document.querySelector('main');
    if (editingIndustry || mediaPickerConfig.isOpen) {
      document.body.style.overflow = 'hidden';
      if (mainEl) mainEl.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      if (mainEl) mainEl.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'unset';
      if (mainEl) mainEl.style.overflow = 'auto';
    };
  }, [editingIndustry, mediaPickerConfig.isOpen]);

  if (loading) {
    return <div className="p-10 text-center font-mono text-xs text-slate-500 animate-pulse">Loading industries data...</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="font-sans text-xl font-bold text-slate-900">Industry Sectors & Applications</h2>
          <p className="font-sans text-xs text-slate-500 mt-1">Manage sector titles, sub-sectors, challenges solved, images, and links.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setEditingIndustry({
              data: {
                id: `industry-${Date.now()}`,
                slug: `industry-${Date.now()}`,
                title: 'New Industry Sector',
                eyebrow: 'MISSION CRITICAL',
                seoTitle: 'Industry Test Systems | Novatek',
                h1: 'Industry Testing Systems',
                shortDesc: '',
                heroDesc: '',
                subSectorsTitle: '',
                viewAllCategory: '',
                primary_image: '',
                gallery: [],
                subSectors: [],
                challengesSolved: [],
                featuredProducts: [],
                featuredCaseStudies: [],
                visible: true
              }
            })}
            className="flex-1 sm:flex-none bg-slate-900 hover:bg-slate-800 text-white font-sans text-xs font-bold px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add Industry
          </button>
          <button
            onClick={handleSaveIndustries}
            disabled={saving}
            className="flex-1 sm:flex-none bg-secondary hover:bg-orange-600 text-white font-sans text-xs font-bold px-5 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 shadow shadow-orange-500/20"
          >
            {saving ? (
              <span className="material-symbols-outlined text-sm animate-spin">sync</span>
            ) : (
              <span className="material-symbols-outlined text-sm">save</span>
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search industries by title, eyebrow, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-secondary transition-colors"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {industriesData
          .filter(ind => {
            if (!ind) return false;
            const query = searchQuery.toLowerCase();
            return (
              (ind.title || '').toLowerCase().includes(query) ||
              (ind.eyebrow || '').toLowerCase().includes(query) ||
              (ind.heroDesc || '').toLowerCase().includes(query) ||
              (ind.shortDesc || '').toLowerCase().includes(query)
            );
          })
          .map((ind) => {
            const originalIdx = industriesData.indexOf(ind);
            return (
          <div key={ind.id || originalIdx} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between group">
            
            <div className="p-5 space-y-3 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-sans font-bold text-secondary uppercase tracking-wider bg-orange-50 px-2 py-0.5 rounded border border-orange-100">
                  {ind.eyebrow || 'SECTOR'}
                </span>
                <span className={`text-[9px] font-sans font-bold px-2 py-0.5 rounded-full border ${
                  ind.visible !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {ind.visible !== false ? 'VISIBLE' : 'HIDDEN'}
                </span>
              </div>
              <div>
                <h4 className="font-sans text-base font-bold text-slate-900 leading-tight">{ind.title}</h4>
                <div className="text-[10px] font-mono text-slate-400 mt-0.5 mb-2 truncate">Slug: {ind.slug}</div>
                <p className="font-sans text-xs text-slate-600 line-clamp-2">{ind.heroDesc || ind.shortDesc}</p>
              </div>

              <div className="text-[10px] font-sans text-slate-500 grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 mt-auto">
                <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[14px]">account_tree</span> {ind.subSectors?.length || 0} Sub-sectors</div>
                <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[14px]">task_alt</span> {ind.challengesSolved?.length || 0} Challenges</div>
                <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[14px]">inventory_2</span> {ind.featuredProducts?.length || 0} Products</div>
                <div className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[14px]">collections</span> {ind.gallery?.length || 0} Gallery Imgs</div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50/80 border-t border-slate-200">
              <div className="flex gap-1.5">
                <button
                  onClick={() => handleMoveIndustry(originalIdx, -1)}
                  disabled={originalIdx === 0}
                  className="p-1.5 rounded-lg border bg-white hover:bg-slate-100 disabled:opacity-30 text-slate-600"
                  title="Move Up"
                >
                  <span className="material-symbols-outlined text-sm">arrow_upward</span>
                </button>
                <button
                  onClick={() => handleMoveIndustry(originalIdx, 1)}
                  disabled={originalIdx === industriesData.length - 1}
                  className="p-1.5 rounded-lg border bg-white hover:bg-slate-100 disabled:opacity-30 text-slate-600"
                  title="Move Down"
                >
                  <span className="material-symbols-outlined text-sm">arrow_downward</span>
                </button>
                <button
                  onClick={() => handleToggleIndustryVisibility(originalIdx)}
                  className={`p-1.5 rounded-lg border hover:bg-slate-100 ${
                    ind.visible !== false ? 'bg-white text-slate-600' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                  }`}
                  title={ind.visible !== false ? 'Hide Industry' : 'Show Industry'}
                >
                  <span className="material-symbols-outlined text-sm">
                    {ind.visible !== false ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setEditingIndustry({ index: originalIdx, data: JSON.parse(JSON.stringify(ind)) })}
                  className="bg-white border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 font-sans text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteIndustry(originalIdx)}
                  className="bg-white border border-red-200 text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                  title="Delete"
                >
                  <span className="material-symbols-outlined text-sm">delete</span>
                </button>
              </div>
            </div>
          </div>
        )})}
        {industriesData.length === 0 ? (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
            <span className="material-symbols-outlined text-4xl text-slate-300 mb-2">factory</span>
            <p className="font-sans text-sm text-slate-500">No industry sectors added yet.</p>
          </div>
        ) : industriesData.filter(ind => {
            const query = searchQuery.toLowerCase();
            return (
              (ind.title || '').toLowerCase().includes(query) ||
              (ind.eyebrow || '').toLowerCase().includes(query) ||
              (ind.heroDesc || '').toLowerCase().includes(query) ||
              (ind.shortDesc || '').toLowerCase().includes(query)
            );
          }).length === 0 && (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
            <span className="material-symbols-outlined text-4xl text-slate-300 mb-2">search</span>
            <p className="font-sans text-sm text-slate-500">No industries found matching your search.</p>
          </div>
        )}
      </div>

      {/* MODAL: Edit Industry */}
      {editingIndustry && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
              <h3 className="font-sans font-bold text-lg text-slate-900">
                {editingIndustry.index !== undefined ? 'Edit Industry Sector' : 'Add New Industry Sector'}
              </h3>
              <button onClick={() => setEditingIndustry(null)} className="text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-lg">
                <span className="material-symbols-outlined text-xl">close</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-8 font-sans text-sm">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Basic Info & Short Description */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingIndustry.data.title || ''}
                      onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, title: e.target.value } })}
                      className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Slug (URL)</label>
                      <input
                        type="text"
                        value={editingIndustry.data.slug || ''}
                        onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, slug: e.target.value } })}
                        className="w-full px-3 py-2 bg-slate-50 border rounded-xl font-mono text-xs focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Eyebrow (Label)</label>
                      <input
                        type="text"
                        value={editingIndustry.data.eyebrow || ''}
                        onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, eyebrow: e.target.value } })}
                        className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Page H1 Heading</label>
                      <input
                        type="text"
                        value={editingIndustry.data.h1 || ''}
                        onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, h1: e.target.value } })}
                        className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">SEO Title</label>
                      <input
                        type="text"
                        value={editingIndustry.data.seoTitle || ''}
                        onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, seoTitle: e.target.value } })}
                        className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Sub-Sectors Section Title</label>
                    <input
                      type="text"
                      placeholder="Default: Application Test Systems & Validation Modules"
                      value={editingIndustry.data.subSectorsTitle || ''}
                      onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, subSectorsTitle: e.target.value } })}
                      className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">View All Systems Target Category</label>
                    <select
                      value={editingIndustry.data.viewAllCategory || ''}
                      onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, viewAllCategory: e.target.value } })}
                      className="w-full px-3 py-2 bg-slate-50 border rounded-xl focus:border-secondary focus:ring-1 focus:ring-secondary transition-all cursor-pointer"
                    >
                      <option value="">-- Select Product Category (Default to splitting title) --</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Short Description (Card Summary)</label>
                    <textarea
                      rows={3}
                      value={editingIndustry.data.shortDesc || ''}
                      onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, shortDesc: e.target.value } })}
                      className="w-full px-3 py-2 bg-slate-50 border rounded-xl resize-none focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                    />
                  </div>
                </div>

                {/* Right Column: Hero Description */}
                <div className="space-y-4">
                  <div className="h-full flex flex-col">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Hero Description (Page Top)</label>
                    <textarea
                      value={editingIndustry.data.heroDesc || ''}
                      onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, heroDesc: e.target.value } })}
                      className="w-full flex-1 min-h-[220px] px-3 py-2 bg-slate-50 border rounded-xl resize-none focus:border-secondary focus:ring-1 focus:ring-secondary transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Gallery Images */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-xs font-bold text-slate-700">Image Gallery (Multiple Images)</label>
                  <button
                    onClick={() => openMediaPicker('gallery')}
                    className="bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-[10px] px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[14px]">add_photo_alternate</span> Add Image
                  </button>
                </div>
                
                {(!editingIndustry.data.gallery || editingIndustry.data.gallery.length === 0) ? (
                  <p className="text-[11px] text-slate-400 font-mono py-4 text-center border border-dashed border-slate-300 rounded-xl">No gallery images added.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {editingIndustry.data.gallery.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-xl bg-slate-200 border border-slate-300 overflow-hidden">
                        <img src={window.resolveMediaUrl(img, 'gallery')} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeGalleryImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <span className="material-symbols-outlined text-[12px]">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sub-Sectors */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-xs font-bold text-slate-700">Sub-Sectors & Applications</label>
                  <button
                    onClick={() => setEditingIndustry({
                      ...editingIndustry,
                      data: {
                        ...editingIndustry.data,
                        subSectors: [...(editingIndustry.data.subSectors || []), { title: '', desc: '', linkedProducts: [] }]
                      }
                    })}
                    className="bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-[10px] px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[14px]">add</span> Add Sub-Sector
                  </button>
                </div>
                
                <div className="space-y-4">
                  {(editingIndustry.data.subSectors || []).map((sub, idx) => (
                    <div key={idx} className="flex flex-col lg:flex-row gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative pr-12">
                      <div className="w-full lg:w-1/2 flex flex-col gap-3">
                        <input
                          type="text"
                          placeholder="Sub-Sector Title"
                          value={sub.title || ''}
                          onChange={(e) => setEditingIndustry({
                            ...editingIndustry,
                            data: {
                              ...editingIndustry.data,
                              subSectors: editingIndustry.data.subSectors.map((item, index) => index === idx ? { ...item, title: e.target.value } : item)
                            }
                          })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 hover:border-slate-300 focus:border-secondary focus:bg-white rounded-xl text-sm font-bold transition-all"
                        />
                        
                        <div className="flex gap-3 h-24">
                          <div 
                            onClick={() => openMediaPicker(`subsector_image_${idx}`)}
                            className="w-24 h-24 shrink-0 rounded-xl border border-slate-200 overflow-hidden cursor-pointer group bg-slate-100 flex items-center justify-center relative hover:border-secondary transition-colors shadow-sm"
                            title="Click to set Cover Image"
                          >
                            {sub.image ? (
                              <img src={window.resolveMediaUrl(sub.image, 'industry')} className="w-full h-full object-cover group-hover:opacity-75 transition-opacity" alt="Cover" />
                            ) : (
                              <span className="material-symbols-outlined text-slate-400 group-hover:text-secondary text-2xl">add_photo_alternate</span>
                            )}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="material-symbols-outlined text-white text-xl">edit</span>
                            </div>
                          </div>
                          <textarea
                            placeholder="Sub-Sector Description..."
                            value={sub.desc || sub.description || ''}
                            onChange={(e) => setEditingIndustry({
                              ...editingIndustry,
                              data: {
                                ...editingIndustry.data,
                                subSectors: editingIndustry.data.subSectors.map((item, index) => index === idx ? { ...item, desc: e.target.value } : item)
                              }
                            })}
                            className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 hover:border-slate-300 focus:border-secondary focus:bg-white rounded-xl text-sm resize-none transition-all"
                          />
                        </div>
                      </div>
                      <div className="w-full lg:w-1/2 flex flex-col gap-2">
                        <div className="text-xs font-bold text-slate-700 flex justify-between items-center">
                          <span>Link Products</span>
                          <span className="text-[10px] font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{(sub.linkedProducts || (sub.linkToProduct ? [sub.linkToProduct] : [])).length} Selected</span>
                        </div>
                        <div className="w-full bg-slate-50 border border-slate-200 rounded-xl custom-scrollbar h-[200px] overflow-y-auto p-2 space-y-1.5">
                          {availableProducts.length === 0 ? (
                            <p className="text-xs text-slate-400 p-4 text-center">No products found.</p>
                          ) : availableProducts.map(prod => {
                            const currentLinks = sub.linkedProducts || (sub.linkToProduct ? [sub.linkToProduct] : []);
                            const isChecked = currentLinks.some(id => id.toString() === prod.id.toString());
                            return (
                              <label key={prod.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${isChecked ? 'bg-orange-50 border-orange-200 shadow-sm' : 'bg-white border-transparent hover:border-slate-200 shadow-sm'}`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    let currentLinked = [...currentLinks].map(id => id.toString());
                                    if (e.target.checked) {
                                      if (!currentLinked.includes(prod.id.toString())) {
                                        currentLinked.push(prod.id.toString());
                                      }
                                    } else {
                                      currentLinked = currentLinked.filter(id => id !== prod.id.toString());
                                    }
                                    setEditingIndustry({
                                      ...editingIndustry,
                                      data: {
                                        ...editingIndustry.data,
                                        subSectors: editingIndustry.data.subSectors.map((item, index) => index === idx ? { ...item, linkedProducts: currentLinked } : item)
                                      }
                                    });
                                  }}
                                  className="w-4 h-4 text-secondary rounded border-slate-300 focus:ring-secondary mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-slate-800 font-bold truncate">{prod.name}</div>
                                  <div className="text-[10px] text-slate-500 truncate">{prod.category || 'Product'}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingIndustry({
                          ...editingIndustry,
                          data: {
                            ...editingIndustry.data,
                            subSectors: editingIndustry.data.subSectors.filter((_, index) => index !== idx)
                          }
                        })}
                        className="absolute right-3 top-4 text-slate-300 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                        title="Remove Sub-Sector"
                      >
                        <span className="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  ))}
                  {(editingIndustry.data.subSectors || []).length === 0 && (
                    <p className="text-[11px] text-slate-400 font-mono py-4 text-center border border-dashed border-slate-300 rounded-xl">No sub-sectors added.</p>
                  )}
                </div>
              </div>

              {/* Challenges Solved */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-xs font-bold text-slate-700">Critical Challenges Solved</label>
                  <button
                    onClick={() => setEditingIndustry({
                      ...editingIndustry,
                      data: {
                        ...editingIndustry.data,
                        challengesSolved: [...(editingIndustry.data.challengesSolved || []), '']
                      }
                    })}
                    className="bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-[10px] px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[14px]">add</span> Add Challenge
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(editingIndustry.data.challengesSolved || []).map((challenge, idx) => (
                    <div key={idx} className="flex items-center gap-2 relative">
                      <input
                        type="text"
                        placeholder={`Challenge ${idx + 1}`}
                        value={challenge}
                        onChange={(e) => setEditingIndustry({
                          ...editingIndustry,
                          data: {
                            ...editingIndustry.data,
                            challengesSolved: editingIndustry.data.challengesSolved.map((item, index) => index === idx ? e.target.value : item)
                          }
                        })}
                        className="w-full px-3 py-2 pr-8 bg-white border border-slate-200 shadow-sm rounded-lg text-xs"
                      />
                      <button
                        onClick={() => setEditingIndustry({
                          ...editingIndustry,
                          data: {
                            ...editingIndustry.data,
                            challengesSolved: editingIndustry.data.challengesSolved.filter((_, index) => index !== idx)
                          }
                        })}
                        className="absolute right-2 text-slate-300 hover:text-red-500 transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">close</span>
                      </button>
                    </div>
                  ))}
                </div>
                {(editingIndustry.data.challengesSolved || []).length === 0 && (
                  <p className="text-[11px] text-slate-400 font-mono py-2 text-center border border-dashed border-slate-300 rounded-xl mt-3">No challenges added.</p>
                )}
              </div>

              {/* Related Products */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-700">Featured Products</label>
                </div>
                <p className="text-[10px] text-slate-500 mb-3 leading-tight">Select products to feature in Equipment Portfolio.</p>
                
                <div className="bg-white border border-slate-200 rounded-xl p-2 h-48 overflow-y-auto custom-scrollbar space-y-1">
                  {availableProducts.length === 0 ? (
                    <p className="text-xs text-slate-400 p-2 text-center">No products found.</p>
                  ) : (
                    availableProducts.map(prod => {
                      const isChecked = (editingIndustry.data.featuredProducts || []).some(id => id.toString() === prod.id.toString());
                      return (
                        <label key={prod.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${isChecked ? 'bg-orange-50 border-orange-200' : 'bg-transparent border-transparent hover:bg-slate-50'}`}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              let selectedOptions = [...(editingIndustry.data.featuredProducts || [])].map(id => id.toString());
                              if (e.target.checked) {
                                if (!selectedOptions.includes(prod.id.toString())) selectedOptions.push(prod.id.toString());
                              } else {
                                selectedOptions = selectedOptions.filter(id => id !== prod.id.toString());
                              }
                              setEditingIndustry({
                                ...editingIndustry,
                                data: { ...editingIndustry.data, featuredProducts: selectedOptions }
                              });
                            }}
                            className="w-4 h-4 text-secondary rounded border-slate-300 focus:ring-secondary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-slate-800 font-bold truncate">{prod.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{prod.category || 'Product'}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              
              {/* Visibility Checkbox */}
              <label className="flex items-center gap-2 p-4 bg-slate-50 rounded-2xl border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={editingIndustry.data.visible !== false}
                  onChange={(e) => setEditingIndustry({ ...editingIndustry, data: { ...editingIndustry.data, visible: e.target.checked } })}
                  className="h-4 w-4 rounded text-secondary"
                />
                <span className="text-sm font-bold text-slate-800">Industry Sector is publicly visible</span>
              </label>

            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => setEditingIndustry(null)}
                className="px-5 py-2.5 rounded-xl font-sans text-xs font-bold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="bg-secondary hover:bg-orange-600 text-white px-6 py-2.5 rounded-xl font-sans text-xs font-bold transition-colors shadow shadow-orange-500/20"
              >
                Done Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reusable Media Picker Instance */}
      <window.MediaPickerModal
        isOpen={mediaPickerConfig.isOpen}
        onClose={() => setMediaPickerConfig({ ...mediaPickerConfig, isOpen: false })}
        onSelect={mediaPickerConfig.onSelect}
        title={mediaPickerConfig.title}
        filterType="images"
      />
    </div>
  );
};
